import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import type { CSSProperties } from 'react';
import type { AIEngineName } from '@internal/spassu-code-contract';
import { WorkerStatusIndicator } from '../WorkerStatusIndicator';
import { ActivityIssue, ActivityWorkItem } from '../../api/types';

const PROVIDER_LABELS: Record<string, string> = {
  'azure-devops': 'Azure DevOps',
  github: 'GitHub',
  gitlab: 'GitLab',
  'bitbucket-cloud': 'Bitbucket',
  'bitbucket-server': 'Bitbucket Server',
  gitea: 'Gitea',
};

export interface ActivityHeaderProps {
  titulo: string;
  responsavel: string;
  projeto: string;
  repositorio: string;
  azureWorkItem?: ActivityWorkItem | null;
  /**
   * Provider id (e.g. 'azure-devops', 'github'). When more than one provider
   * is registered this drives a small badge so users can tell at a glance
   * which SCM the activity belongs to.
   */
  provider?: string;
  /** Provider-agnostic issue link. Renders when no `azureWorkItem` is set. */
  issue?: ActivityIssue | null;
  /** Whether to display the provider badge — only true when >1 provider. */
  showProviderBadge?: boolean;
  /**
   * AI engine ativa para esta atividade (D1). Mostra o chip da engine no
   * header para o usuário saber qual motor é cobrado pelo consumo desta
   * atividade. Quando undefined, o chip não aparece.
   */
  aiEngine?: AIEngineName;
  /**
   * Modelo efetivo da fase atual, já resolvido pelo backend com a precedência
   * da iniciativa `usar-modelos-worker` (preferência do worker → fase →
   * default da engine). Exibido como id técnico, ao lado da engine, porque é
   * o mesmo id que aparece em métricas e logs. Quando undefined (backend
   * antigo), o chip não aparece.
   */
  model?: string | null;
}

// Record exaustivo por AIEngineName: engine nova sem entrada aqui é erro de
// compilação (antes, gates por `=== 'codex'` jogavam engine nova no ramo
// errado em silêncio — iniciativa integracao-github-copilot).
export const AI_ENGINE_LABELS: Record<AIEngineName, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  'github-copilot': 'GitHub Copilot',
};

/**
 * Chip da engine: mesma identidade visual para TODAS as engines (laranja da
 * marca), porque o dado relevante é qual motor executa — não uma hierarquia
 * entre eles. Antes só o Claude era colorido e Codex/Copilot ficavam cinza,
 * indistinguíveis entre si. Engine nova entra sem mudança aqui.
 */
const AI_ENGINE_CHIP_STYLE: CSSProperties = {
  backgroundColor: '#FA461620',
  color: '#FA4616',
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  brandRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
  },
  brand: {
    fontFamily: "'Lexend Peta', 'Poppins', sans-serif",
    fontWeight: 700,
    fontSize: '0.78rem',
    color: theme.palette.secondary.main,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  separator: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: '0.78rem',
  },
  frontier: {
    fontFamily: "'Lexend Peta', 'Poppins', sans-serif",
    fontWeight: 400,
    fontSize: '0.78rem',
    color: theme.palette.text.primary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  activityTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '1.125rem',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
    letterSpacing: 0,
    wordBreak: 'break-word' as const,
  },
  metaInline: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  metaText: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  dot: {
    fontSize: '0.3rem',
    color: theme.palette.text.disabled,
  },
  right: {
    display: 'flex',
    alignItems: 'flex-start',
    flexShrink: 0,
    paddingTop: theme.spacing(0.5),
  },
  wiCard: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.78rem',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  wiId: {
    fontWeight: 700,
    color: theme.palette.secondary.main,
  },
  wiType: {
    color: theme.palette.text.secondary,
  },
  wiTitle: {
    maxWidth: 250,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  wiIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
}));

export const ActivityHeader = ({
  titulo,
  responsavel,
  projeto,
  repositorio,
  azureWorkItem,
  provider,
  issue,
  showProviderBadge,
  aiEngine,
  model,
}: ActivityHeaderProps) => {
  const classes = useStyles();
  const providerLabel = provider ? PROVIDER_LABELS[provider] ?? provider : null;
  const aiEngineLabel = aiEngine ? AI_ENGINE_LABELS[aiEngine] : null;
  const modelLabel = model?.trim() || null;

  return (
    <div className={classes.root}>
      <div className={classes.left}>
        <div className={classes.brandRow}>
          <span className={classes.brand}>SPASSU CODE</span>
          <span className={classes.separator}>/</span>
          <span className={classes.frontier}>FRONTIER</span>
          {showProviderBadge && providerLabel && (
            <Chip
              data-testid="provider-badge"
              label={providerLabel}
              size="small"
              variant="outlined"
              style={{ marginLeft: 6, height: 18, fontSize: '0.65rem' }}
            />
          )}
          {aiEngine && aiEngineLabel && (
            <Chip
              data-testid="ai-engine-badge"
              label={aiEngineLabel}
              size="small"
              style={{
                marginLeft: 6,
                height: 18,
                fontSize: '0.65rem',
                ...AI_ENGINE_CHIP_STYLE,
              }}
            />
          )}
          {modelLabel && (
            <Chip
              data-testid="ai-model-badge"
              label={modelLabel}
              size="small"
              variant="outlined"
              style={{ marginLeft: 6, height: 18, fontSize: '0.65rem' }}
            />
          )}
          <span className={classes.metaInline}>
            <Typography component="span" className={classes.metaText}>
              {responsavel}
            </Typography>
            <span className={classes.dot}>&#9679;</span>
            <Typography component="span" className={classes.metaText}>
              {projeto}
            </Typography>
            <span className={classes.dot}>&#9679;</span>
            <Typography component="span" className={classes.metaText}>
              {repositorio}
            </Typography>
          </span>
        </div>
        <Typography
          variant="h4"
          component="h1"
          className={classes.activityTitle}
        >
          {titulo}
        </Typography>
        {azureWorkItem && (
          <Tooltip
            title={`${azureWorkItem.title} (${azureWorkItem.state})`}
            arrow
          >
            <a
              href={azureWorkItem.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.wiCard}
              aria-label={`Ver no Azure DevOps: #${azureWorkItem.id}`}
            >
              <span className={classes.wiId}>#{azureWorkItem.id}</span>
              <span className={classes.wiType}>· {azureWorkItem.type}</span>
              <span className={classes.wiTitle}>{azureWorkItem.title}</span>
              <Chip
                label={azureWorkItem.state}
                size="small"
                style={{ height: 18, fontSize: '0.65rem' }}
              />
              <OpenInNewIcon className={classes.wiIcon} />
            </a>
          </Tooltip>
        )}
        {!azureWorkItem && issue && (
          <Tooltip title={`${issue.title} (${issue.state})`} arrow>
            <a
              href={issue.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.wiCard}
              aria-label={`Ver issue: #${issue.externalId}`}
            >
              <span className={classes.wiId}>#{issue.externalId}</span>
              {issue.type && (
                <span className={classes.wiType}>· {issue.type}</span>
              )}
              <span className={classes.wiTitle}>{issue.title}</span>
              <Chip
                label={issue.state}
                size="small"
                style={{ height: 18, fontSize: '0.65rem' }}
              />
              <OpenInNewIcon className={classes.wiIcon} />
            </a>
          </Tooltip>
        )}
      </div>
      <div className={classes.right}>
        <WorkerStatusIndicator />
      </div>
    </div>
  );
};
